import { useState, useMemo } from "react";
import { Filter, X, ChevronDown, ChevronUp, Check, Plus, LayoutGrid, List } from "lucide-react";
import { CarCard } from "@/components/cars/CarCard";
import { CarCardList } from "@/components/cars/CarCardList";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { useI18n } from "@/lib/i18n";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type SortOption = "price_asc" | "price_desc" | "year_desc" | "km_asc";

interface Filters {
  brand?: string;
  fuel?: string;
  transmission?: string;
  segment?: string;
  minYear?: string;
  maxYear?: string;
  minPrice?: string;
  maxPrice?: string;
  minKm?: string;
  maxKm?: string;
}

const ITEMS_DESKTOP = 9;
const ITEMS_MOBILE = 5;

export function StockSection() {
  const { t } = useI18n();
  const [filters, setFilters] = useState<Filters>({}); // Filtros aplicados
  const [tempFilters, setTempFilters] = useState<Filters>({}); // Filtros em edição
  const [sort, setSort] = useState<SortOption>("price_asc");
  const [visibleCount, setVisibleCount] = useState(0);
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileView, setMobileView] = useState<'grid' | 'list'>('grid');
  const { data: cars, isLoading } = useQuery({
    queryKey: ["stock-cars"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cars")
        .select(`*, car_images(storage_path, storage_path_avif, storage_path_webp, is_primary, position)`)
        .eq("status", "available")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const filterOptions = useMemo(() => {
    if (!cars) return { brands: [], fuels: [], transmissions: [], segments: [] };
    return {
      brands: [...new Set(cars.map((c) => c.brand))].sort(),
      fuels: [...new Set(cars.map((c) => c.fuel))],
      transmissions: [...new Set(cars.map((c) => c.transmission))],
      segments: [...new Set(cars.map((c) => c.segment))],
    };
  }, [cars]);

  const filteredCars = useMemo(() => {
    if (!cars) return [];
    let result = cars.filter((car) => {
      if (filters.brand && car.brand !== filters.brand) return false;
      if (filters.fuel && car.fuel !== filters.fuel) return false;
      if (filters.transmission && car.transmission !== filters.transmission) return false;
      if (filters.segment && car.segment !== filters.segment) return false;
      if (filters.minYear && car.year < parseInt(filters.minYear)) return false;
      if (filters.maxYear && car.year > parseInt(filters.maxYear)) return false;
      if (filters.minPrice && Number(car.price) < parseInt(filters.minPrice)) return false;
      if (filters.maxPrice && Number(car.price) > parseInt(filters.maxPrice)) return false;
      if (filters.minKm && car.km < parseInt(filters.minKm)) return false;
      if (filters.maxKm && car.km > parseInt(filters.maxKm)) return false;
      return true;
    });

    result.sort((a, b) => {
      switch (sort) {
        case "price_asc":
          return Number(a.price) - Number(b.price);
        case "price_desc":
          return Number(b.price) - Number(a.price);
        case "year_desc":
          return b.year - a.year;
        case "km_asc":
          return a.km - b.km;
        default:
          return 0;
      }
    });
    return result;
  }, [cars, filters, sort]);

  const itemsPerLoad = isMobile ? ITEMS_MOBILE : ITEMS_DESKTOP;
  const currentLimit = visibleCount === 0 ? itemsPerLoad : visibleCount;
  const visibleCars = filteredCars.slice(0, currentLimit);
  const remainingCount = filteredCars.length - visibleCars.length;

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // Atualiza apenas o estado temporário enquanto o user digita
  const updateTempFilter = (key: keyof Filters, value: string | undefined) => {
    setTempFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Aplica os filtros temporários ao estado real
  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setVisibleCount(0);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilters({});
    setTempFilters({});
    setVisibleCount(0);
  };

  const filtersInputs = (
    <div className="space-y-6">
      <div className="space-y-4">
        {filterOptions.brands.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">{t.stock.brand}</label>
            <Select
              value={tempFilters.brand || "all"}
              onValueChange={(v) => updateTempFilter("brand", v === "all" ? undefined : v)}
            >
              <SelectTrigger aria-label={t.stock.brand}>
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {filterOptions.brands.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Preço (€)</label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Min"
                className="h-9"
                value={tempFilters.minPrice || ""}
                onChange={(e) => updateTempFilter("minPrice", e.target.value)}
              />
              <Input
                type="number"
                placeholder="Max"
                className="h-9"
                value={tempFilters.maxPrice || ""}
                onChange={(e) => updateTempFilter("maxPrice", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Ano</label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Desde"
                className="h-9"
                value={tempFilters.minYear || ""}
                onChange={(e) => updateTempFilter("minYear", e.target.value)}
              />
              <Input
                type="number"
                placeholder="Até"
                className="h-9"
                value={tempFilters.maxYear || ""}
                onChange={(e) => updateTempFilter("maxYear", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Quilómetros (km)</label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Min"
                className="h-9"
                value={tempFilters.minKm || ""}
                onChange={(e) => updateTempFilter("minKm", e.target.value)}
              />
              <Input
                type="number"
                placeholder="Max"
                className="h-9"
                value={tempFilters.maxKm || ""}
                onChange={(e) => updateTempFilter("maxKm", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 pt-2">
          {filterOptions.fuels.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">{t.stock.fuel}</label>
              <Select
                value={tempFilters.fuel || "all"}
                onValueChange={(v) => updateTempFilter("fuel", v === "all" ? undefined : v)}
              >
                <SelectTrigger aria-label={t.stock.fuel}>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filterOptions.fuels.map((f) => (
                    <SelectItem key={f} value={f}>
                      {t.fuel[f as keyof typeof t.fuel] || f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterOptions.transmissions.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">{t.stock.transmission}</label>
              <Select
                value={tempFilters.transmission || "all"}
                onValueChange={(v) => updateTempFilter("transmission", v === "all" ? undefined : v)}
              >
                <SelectTrigger aria-label={t.stock.transmission}>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filterOptions.transmissions.map((tr) => (
                    <SelectItem key={tr} value={tr}>
                      {tr === "manual" ? t.stock.manual : t.stock.automatic}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-2">
        <Button onClick={handleApplyFilters} className="w-full gap-2">
          <Check className="h-4 w-4" /> Aplicar Filtros
        </Button>

        {activeFilterCount > 0 && (
          <Button variant="ghost" onClick={clearFilters} className="w-full text-muted-foreground text-xs h-8">
            <X className="h-3 w-3 mr-1" /> Limpar tudo
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <section id="stock" className="py-16 bg-background">
      <div className="container mx-auto px-4">
        {/* Mobile layout: title → count+sort → filters+view */}
        <div className="lg:hidden mb-6 space-y-3">
          <h2 className="text-2xl font-bold">O Nosso Stock</h2>
          <div className="flex items-center justify-between gap-3">
            <Badge variant="outline" className="text-muted-foreground font-normal shrink-0">
              {filteredCars.length} veículos encontrados
            </Badge>
            <Select value={sort} onValueChange={(v: SortOption) => setSort(v)}>
              <SelectTrigger className="w-44" aria-label="Ordenar por">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="price_asc">{t.stock.priceAsc}</SelectItem>
                <SelectItem value="price_desc">{t.stock.priceDesc}</SelectItem>
                <SelectItem value="year_desc">{t.stock.yearDesc}</SelectItem>
                <SelectItem value="km_asc">{t.stock.kmAsc}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Desktop layout: title + count + sort in one row */}
        <div className="hidden lg:flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold">O Nosso Stock</h2>
            <Badge variant="outline" className="text-muted-foreground font-normal">
              {filteredCars.length} veículos encontrados
            </Badge>
          </div>
          <Select value={sort} onValueChange={(v: SortOption) => setSort(v)}>
            <SelectTrigger className="w-48" aria-label="Ordenar por">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="price_asc">{t.stock.priceAsc}</SelectItem>
              <SelectItem value="price_desc">{t.stock.priceDesc}</SelectItem>
              <SelectItem value="year_desc">{t.stock.yearDesc}</SelectItem>
              <SelectItem value="km_asc">{t.stock.kmAsc}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="glass-card p-6 sticky top-24 border border-border/50 shadow-sm rounded-2xl bg-card">
              <h3 className="font-semibold mb-6 flex items-center gap-2">
                <Filter className="h-4 w-4 text-primary" />
                {t.stock.filters}
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-auto bg-primary/10 text-primary border-none">
                    {activeFilterCount}
                  </Badge>
                )}
              </h3>
              {filtersInputs}
            </div>
          </aside>

          <div className="lg:hidden">
            <div className="flex gap-2 mb-4">
              <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen} className="flex-1">
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full gap-2 h-12">
                    <Filter className="h-4 w-4" />
                    {filtersOpen ? "Fechar Filtros" : "Mostrar Filtros"}
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {activeFilterCount}
                      </Badge>
                    )}
                    {filtersOpen ? (
                      <ChevronUp className="h-4 w-4 ml-auto" />
                    ) : (
                      <ChevronDown className="h-4 w-4 ml-auto" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="glass-card p-4 mt-2 mb-2 border border-border/50 rounded-2xl shadow-sm bg-card">
                    {filtersInputs}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <div className="flex border border-border rounded-lg overflow-hidden shrink-0 h-12">
                <Button
                  variant={mobileView === 'grid' ? 'default' : 'ghost'}
                  size="icon"
                  className="h-full w-11 rounded-none"
                  onClick={() => setMobileView('grid')}
                  aria-label="Vista em grelha"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={mobileView === 'list' ? 'default' : 'ghost'}
                  size="icon"
                  className="h-full w-11 rounded-none"
                  onClick={() => setMobileView('list')}
                  aria-label="Vista em lista"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <main className="flex-1">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-[400px] rounded-3xl" />
                ))}
              </div>
            ) : filteredCars.length === 0 ? (
              <div className="glass-card p-12 text-center border-dashed border-2 rounded-3xl">
                <p className="text-muted-foreground text-lg">Nenhum veículo encontrado com estes filtros.</p>
                <Button variant="outline" onClick={clearFilters} className="mt-4">
                  Limpar todos os filtros
                </Button>
              </div>
            ) : (
              <>
                {/* Desktop always grid, mobile respects toggle */}
                <div className="hidden lg:grid grid-cols-2 xl:grid-cols-3 gap-6">
                  {visibleCars.map((car) => (
                    <CarCard key={car.id} car={car} />
                  ))}
                </div>

                {mobileView === 'grid' ? (
                  <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-6">
                    {visibleCars.map((car) => (
                      <CarCard key={car.id} car={car} />
                    ))}
                  </div>
                ) : (
                  <div className="lg:hidden flex flex-col gap-3">
                    {visibleCars.map((car) => (
                      <CarCardList key={car.id} car={car} />
                    ))}
                  </div>
                )}

                {remainingCount > 0 && (
                  <div className="mt-12 flex justify-center">
                    <Button
                      variant="outline"
                      size="lg"
                      className="gap-2 rounded-full border-primary text-primary-text hover:bg-primary hover:text-primary-foreground px-8"
                      onClick={() => setVisibleCount(currentLimit + itemsPerLoad)}
                    >
                      <Plus className="h-5 w-5" />
                      Carregar mais {Math.min(remainingCount, itemsPerLoad)} viaturas
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </section>
  );
}
